import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useCreateQuietPeriod } from "../../hooks/useQuietPeriods";
import { QuietPeriodCreate } from "../../types/quiet_period";
import { Input, Textarea } from "../../components/ui/Input";

const CreateQuietPeriodPage: React.FC = () => {
  const navigate = useNavigate();
  const createMutation = useCreateQuietPeriod();
  const [form, setForm] = useState<QuietPeriodCreate>({
    name: "",
    description: "",
    from: "",
    to: "",
    tags: [],
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field: keyof QuietPeriodCreate, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: QuietPeriodCreate = {
        name: form.name.trim(),
        description: form.description || undefined,
        from: form.from,
        to: form.to,
        tags: (form.tags || []).map((t) => t.trim()).filter(Boolean),
      };
      const created = await createMutation.mutateAsync(payload);
      navigate(`/quiet-periods/${created.id}`);
    } finally {
      setSaving(false);
    }
  };

  const tagsInput = (form.tags || []).join(", ");

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Create Dynamic Date
        </h2>
        <p className="text-sm text-gray-500">
          Define a new dynamic date window or holiday blackout
        </p>
      </div>

      <form
        className="bg-white shadow sm:rounded-lg p-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <Input
          label="Name"
          type="text"
          value={form.name}
          onChange={(e) => handleChange("name", e.target.value)}
          required
          variant="rounded-md"
        />
        <Textarea
          label="Description"
          rows={3}
          value={form.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          variant="rounded-md"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="From"
            type="datetime-local"
            value={form.from}
            onChange={(e) => handleChange("from", e.target.value)}
            required
            variant="rounded-md"
          />
          <Input
            label="To"
            type="datetime-local"
            value={form.to}
            onChange={(e) => handleChange("to", e.target.value)}
            required
            variant="rounded-md"
          />
        </div>

        <Input
          label="Tags"
          type="text"
          placeholder="e.g. holiday, maintenance"
          value={tagsInput}
          onChange={(e) =>
            handleChange(
              "tags",
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          variant="rounded-md"
          helperText="Comma-separated list. Used by transitions and date pickers to exclude dates."
        />

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="btn-secondary px-4 py-2 rounded-md"
            onClick={() => navigate("/quiet-periods")}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary px-4 py-2 rounded-md"
          >
            {saving ? "Saving..." : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateQuietPeriodPage;
